import { useCallback, useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { firestore, FIRESTORE_COLLECTIONS } from '../firebase';
import type { VenueSummary } from '../types';
import { normalizeOwnedPartnerVenue } from './partnerProfiles';
import { isPartner } from './roles';

// Whether a Tonyte account is a partner, and which partner profiles it runs.
// Both halves come from admin-controlled records — users/{uid}.role ==
// 'partner' and venues/{id}.ownerUid == uid — never from a display name, so a
// member called "OpenSpace" is still a member.
export type PartnerIdentity =
  | { kind: 'personal' }
  | { kind: 'partner'; venues: VenueSummary[] };

const PERSONAL: PartnerIdentity = { kind: 'personal' };
const CACHE_TTL_MS = 5 * 60 * 1000;
const cache = new Map<string, { identity: PartnerIdentity; at: number }>();
const pending = new Map<string, Promise<PartnerIdentity>>();

export function getCachedPartnerIdentity(userId: string | null | undefined): PartnerIdentity | null {
  const entry = userId ? cache.get(userId) : undefined;
  return entry && Date.now() - entry.at < CACHE_TTL_MS ? entry.identity : null;
}

/** Throws on read failures so callers can tell "not a partner" from "couldn't check". */
export async function resolvePartnerIdentity(userId: string): Promise<PartnerIdentity> {
  const cached = getCachedPartnerIdentity(userId);
  if (cached) return cached;
  const inFlight = pending.get(userId);
  if (inFlight) return inFlight;

  const promise = (async () => {
    const userSnap = await getDoc(doc(firestore, FIRESTORE_COLLECTIONS.users, userId));
    let identity = PERSONAL;
    if (userSnap.exists() && isPartner(userSnap.data().role)) {
      const venues = await getDocs(query(collection(firestore, 'venues'), where('ownerUid', '==', userId)));
      identity = {
        kind: 'partner',
        venues: venues.docs
          .map(item => normalizeOwnedPartnerVenue(item.id, item.data()))
          .sort((a, b) => a.name.localeCompare(b.name)),
      };
    }
    cache.set(userId, { identity, at: Date.now() });
    return identity;
  })().finally(() => pending.delete(userId));

  pending.set(userId, promise);
  return promise;
}

/** Partner profiles this viewer may open (internal test venues are admin-only). */
export function visiblePartnerVenues(identity: PartnerIdentity | null, canSeeAdminOnly: boolean): VenueSummary[] {
  if (!identity || identity.kind !== 'partner') return [];
  return identity.venues.filter(venue => !venue.adminOnly || canSeeAdminOnly);
}

export function usePartnerIdentity(userId: string | null | undefined) {
  const id = typeof userId === 'string' ? userId.trim() : '';
  const [nonce, setNonce] = useState(0);
  const [state, setState] = useState<{ identity: PartnerIdentity | null; error: Error | null }>(() => ({
    identity: getCachedPartnerIdentity(id),
    error: null,
  }));

  useEffect(() => {
    if (!id) {
      setState({ identity: null, error: null });
      return undefined;
    }
    const cached = getCachedPartnerIdentity(id);
    if (cached) {
      setState({ identity: cached, error: null });
      return undefined;
    }
    let active = true;
    setState({ identity: null, error: null });
    resolvePartnerIdentity(id)
      .then(identity => { if (active) setState({ identity, error: null }); })
      .catch(error => {
        console.warn('[Tonyte][RN][PartnerIdentity] resolve failed', { userId: id, error });
        if (active) setState({ identity: null, error: error instanceof Error ? error : new Error(String(error)) });
      });
    return () => { active = false; };
  }, [id, nonce]);

  const retry = useCallback(() => setNonce(value => value + 1), []);
  return { ...state, loading: Boolean(id) && !state.identity && !state.error, retry };
}
